import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// 메뉴 아이템 인터페이스
interface MenuItem {
  id: string;              // 메뉴 고유 ID
  title: string;           // 메뉴 제목
  hasIcon?: boolean;       // 아이콘 포함 여부
  isSelected?: boolean;    // 현재 선택된 메뉴인지 여부
  path?: string;           // 메뉴 경로
  subItems?: MenuItem[];   // 하위 메뉴 아이템들
}

// 메인 메뉴 props 인터페이스
interface MainMenuProps {
  items: MenuItem[];                    // 메뉴 아이템들
  selectedItemId?: string;              // 현재 선택된 메뉴 ID
  onMenuClick: (itemId: string, path?: string) => void; // 메뉴 클릭 시 호출되는 함수
  className?: string;                   // 추가 CSS 클래스
  textColor?: string;
}

// MainMenu 컴포넌트 정의
const MainMenu: React.FC<MainMenuProps> = ({ 
  items,              // 메뉴 아이템들
  selectedItemId,     // 현재 선택된 메뉴 ID
  onMenuClick,        // 메뉴 클릭 함수
  className = '',
  textColor = 'var(--Color-fill-normal, #293044)',
}) => {
  // 하위메뉴 열림/닫힘 상태 관리
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const location = useLocation();

  // 현재 경로가 서브메뉴 경로와 일치하지 않으면 서브메뉴 닫기
  useEffect(() => {
    let foundMatchingSubmenu = false;
    
    items.forEach((item) => {
      if (item.subItems && item.subItems.length > 0) {
        // 서브메뉴 중 하나라도 현재 경로와 일치하는지 확인만 함
        const hasMatchingSubmenu = item.subItems.some((subItem) => {
          if (!subItem.path) return false;
          return location.pathname === subItem.path
            || location.pathname.startsWith(`${subItem.path}/`);
        });
        if (hasMatchingSubmenu) {
          foundMatchingSubmenu = true;
        }
      }
    });
    
    // 현재 경로가 어떤 서브메뉴 경로와도 일치하지 않으면 서브메뉴 닫기
    if (!foundMatchingSubmenu) {
      setOpenSubmenu(null);
    }
  }, [location.pathname, items]);

  // 메뉴 클릭 핸들러
  const handleMenuClick = (item: MenuItem) => {
    if (item.subItems && item.subItems.length > 0) {
      // 하위메뉴가 있는 경우 하위메뉴 토글
      setOpenSubmenu(openSubmenu === item.id ? null : item.id);
    } else {
      // 하위메뉴가 없는 경우 직접 페이지 이동하고 열려있던 서브메뉴 닫기
      setOpenSubmenu(null);
      onMenuClick(item.id, item.path);
    }
  };

  // 하위메뉴 아이템 클릭 핸들러
  const handleSubmenuClick = (subItem: MenuItem, parentItemId: string) => {
    onMenuClick(subItem.id, subItem.path);
    // 서브메뉴 닫기 - useEffect가 경로를 감지해서 자동으로 다시 열어줌
    setOpenSubmenu(null);
  };

  return (
    <div 
      style={{
        // Figma에서 가져온 컨테이너 스타일
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 16,                    // Figma에서 가져온 간격
        display: 'flex'
      }}
      className={className}
    >
      {items.map((item) => {
        const isSelected = item.id === selectedItemId
          || (item.subItems?.some((sub) => sub.id === selectedItemId) ?? false);
        const isSubmenuOpen = openSubmenu === item.id;
        
        return (
          <div 
            key={item.id}
            style={{
              // Figma에서 가져온 메뉴 아이템 컨테이너 스타일
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,                    // Figma에서 가져온 간격
              display: 'inline-flex',
              position: 'relative'       // 하위메뉴 위치 기준점
            }}
          >
            <div 
              style={{
                // Figma에서 가져온 메뉴 버튼 스타일
                height: 34,                              // Figma에서 가져온 높이
                flexDirection: 'column',        // 세로 방향 배치
                justifyContent: 'center',       // 글자를 옆 학급/교과 토글과 같은 높이(칸 가운데)에 맞춘다
                alignItems: 'flex-start',       // 좌측 정렬
                display: 'flex',                // flexbox 레이아웃
                flexShrink: 0,
                cursor: 'pointer',                       // 마우스 커서 변경, 일단 내맘대로
                transition: 'all 0.2s ease'              // 부드러운 전환 효과, 일단 내맘대로
              }}
              onClick={() => handleMenuClick(item)}       // 클릭 시 메뉴 클릭 함수 호출
            >
              <div style={{
                // Figma에서 가져온 메뉴 내용 스타일
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                display: 'flex',
                position: 'relative'                     // 하단 바를 글자 아래에 띄워 글자 위치가 흔들리지 않게 한다
              }}>
                <div style={{
                  // Figma에서 가져온 메뉴 텍스트 영역 스타일
                  paddingLeft: 12,                       // Figma에서 가져온 좌측 패딩
                  paddingRight: 12,                      // Figma에서 가져온 우측 패딩
                  justifyContent: 'center',     // 가로 중앙 정렬
                  alignItems: 'center',         // 세로 중앙 정렬
                  gap: 8,                                // Figma에서 가져온 간격
                  display: 'inline-flex',
                  whiteSpace: 'nowrap'
                }}>
                  <div style={{
                    // Figma에서 가져온 메뉴 텍스트 스타일
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    color: isSelected 
                      ? 'var(--Color-fill-brand-color, #FF6F0F)'  // 선택됨: 오렌지색
                      : textColor,
                    fontSize: 14,                                 // Figma에서 가져온 폰트 크기
                    fontFamily: 'Pretendard',                      // Figma에서 사용하는 폰트
                    fontWeight: '500',                             // Figma에서 가져온 폰트 굵기
                    lineHeight: '21px',                            // Figma에서 가져온 줄 간격
                    whiteSpace: 'nowrap'
                  }}>
                    {item.title}
                  </div>
                  
                  {/* 아이콘이 있는 메뉴의 경우 화살표 아이콘 표시 */}
                  {item.hasIcon && (
                    <div style={{
                      width: 16,
                      height: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transform: isSubmenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}>
                      <img 
                        src="/images/icons/name=down.svg" 
                        alt="화살표" 
                        style={{
                          width: 16,
                          height: 16,
                          filter: 'brightness(0) saturate(100%)' // 색상을 CSS 변수로 변경 가능
                        }}
                      />
                    </div>
                  )}
                </div>
                
                {/* 선택된 메뉴의 경우 하단 바 표시 */}
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: 2,                           // Figma에서 가져온 글자-바 간격
                  width: 18,                              // Figma에서 가져온 바 너비
                  height: isSelected ? 2 : 0,             // 선택됨: 2px, 선택 안됨: 0px
                  background: isSelected 
                    ? 'var(--Color-fill-brand-color, #FF6F0F)'  // 선택됨: 오렌지색
                    : 'transparent',                            // 선택 안됨: 투명
                  transition: 'height 0.2s ease'                 // 부드러운 전환 효과
                }} />
              </div>
            </div>

            {/* 하위메뉴 드롭다운 */}
            {item.subItems && item.subItems.length > 0 && isSubmenuOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 1000,
                minWidth: '200px',
                padding: '8px 0',
                marginTop: '8px'
              }}>
                {item.subItems.map((subItem) => {
                  const isSubSelected = subItem.id === selectedItemId;
                  return (
                  <div
                    key={subItem.id}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: isSubSelected ? 600 : 400,
                      color: isSubSelected ? '#FF6F0F' : '#293044',
                      backgroundColor: isSubSelected ? '#FFF4ED' : 'transparent',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubSelected) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubSelected) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                    onClick={() => handleSubmenuClick(subItem, item.id)}
                  >
                    {subItem.title}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// MainMenu 컴포넌트를 다른 파일에서 사용할 수 있도록 내보내기
export default MainMenu;
